import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  awaitPendingMessage,
  sendTestMessage,
} from 'app/frontend/base/api/notification'
import { deleteRequest, putRequest } from 'app/frontend/base/api'
import { useTeam } from 'app/frontend/base/config/context/team'
import { displayErrorToast } from 'app/frontend/base/helpers'

type Options = {
  onActionComplete?: () => void
}

export const useNotificationTemplateActions = (
  template: Partial<wowaudit.NotificationTemplate>,
  { onActionComplete }: Options = {},
) => {
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [testMessageLoading, setTestMessageLoading] = useState(false)
  const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false)
  const [suspendLoading, setSuspendLoading] = useState(false)

  const completeTestMessage = () => {
    setTestMessageLoading(false)
    onActionComplete?.()
    queryClient.invalidateQueries({
      queryKey: ['notifications', { id: team.id }],
    })
  }

  const triggerTestMessage = async () => {
    if (!template.id) return
    setTestMessageLoading(true)

    try {
      const result = await sendTestMessage(template.id, team.id)

      if (result.notification_id) {
        awaitPendingMessage(team.id, result.notification_id, (notification) => {
          const { deliveryStatus, errorStatus } = notification

          if (deliveryStatus === 'delivered') {
            toast.success('Test message sent successfully')
          } else {
            toast.error(
              `Failed to deliver test message. ${errorStatus} (${deliveryStatus})`,
            )
          }

          completeTestMessage()
        })
      } else {
        toast.error('Failed to schedule test message')
        completeTestMessage()
      }
    } catch (error) {
      displayErrorToast(error as Error, t)
      completeTestMessage()
    }
  }

  const setTemplateSuspended = async (suspended: boolean) => {
    if (!template.id || suspendLoading) return

    setSuspendLoading(true)

    try {
      const updated = (await putRequest(
        `/api/teams/${team.id}/notification_templates/${template.id}`,
        { notification_template: { suspended } },
      )) as wowaudit.NotificationTemplate

      queryClient.setQueryData<wowaudit.NotificationTemplate[]>(
        ['notificationTemplates', { id: team.id }],
        (current) =>
          current?.map((item) =>
            item.id === template.id ? { ...item, ...updated, suspended } : item,
          ),
      )
      queryClient.invalidateQueries({
        queryKey: ['notificationTemplates', { id: team.id }],
      })

      return updated
    } catch (error) {
      displayErrorToast(error as Error, t)
    } finally {
      setSuspendLoading(false)
    }
  }

  const triggerDeleteTemplate = async () => {
    if (!template.id || deleteTemplateLoading) return
    if (!confirm(t('shared.delete-confirm'))) return
    setDeleteTemplateLoading(true)

    try {
      await deleteRequest(
        `/api/teams/${team.id}/notification_templates/${template.id}`,
      )
      queryClient.invalidateQueries({
        queryKey: ['notificationTemplates', { id: team.id }],
      })
      onActionComplete?.()
    } catch (error) {
      displayErrorToast(error as Error, t)
    } finally {
      setDeleteTemplateLoading(false)
    }
  }

  return {
    triggerTestMessage,
    testMessageLoading,
    triggerDeleteTemplate,
    deleteTemplateLoading,
    setTemplateSuspended,
    suspendLoading,
  }
}
